import React, { useEffect, useMemo, useState, memo, useCallback } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { MenuItem, Select } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid } from "date-fns";
import { getUserDateFormat, convertToDateFnsFormat } from "../../utils/formatDate.js";
import FleetMapView from "../dashboard/FleetMapView";
import { reportsApi, getOrganizationId } from "../manageData/reportsApi.js";
import { EMPTY_CHART_ROWS, useChartPeriod, useChartValue } from "../common/useChartPeriod";
import {
  isInactiveState,
  isOnOffDevice,
  isSensorAlert,
  isSensorNearThreshold,
} from "../../utils/deviceStatusHelpers.js";
import {
  DEFAULT_SENSOR_RANGES,
  normalizeSensorType,
} from "../../utils/vehicleMonitorData.js";

const COLORS = ["#3b82f6", "#22c55e", "#a855f7", "#f59e0b", "#06b6d4", "#ef4444"];

export const CUSTOM_DATE_OPTION = "custom";

export const DASHBOARD_DATE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last 7 days", label: "Last 7 Days" },
  { value: "last 14 days", label: "Last 14 Days" },
  { value: "last 30 days", label: "Last 30 Days" },
  { value: "this week", label: "This Week" },
  { value: "this month", label: "This Month" },
  { value: CUSTOM_DATE_OPTION, label: "Custom Range" },
];

const ALERT_COLORS = {
  good: "#16a34a",
  warning: "#f59e0b",
  danger: "#ef4444",
};

const CHART_TOOLTIP_STYLE = {
  position: "fixed",
  zIndex: 40,
  pointerEvents: "none",
  backgroundColor: "#ffffff",
  border: "1px solid #DBEAFE",
  borderRadius: 4,
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
  fontSize: 11,
  padding: "6px 8px",
  color: "#0f172a",
  transform: "translate(12px, 12px)",
  whiteSpace: "nowrap",
};

const getRouteName = (vehicle) => vehicle.route?.name || vehicle.route?.Name || "";

const getVehicleType = (vehicle) =>
  vehicle.type ||
  vehicle.vehicleType ||
  vehicle.VehicleType ||
  vehicle.category ||
  vehicle.vehicle_category ||
  "Unknown";

const getFuelType = (vehicle) =>
  vehicle.fuelType || vehicle.fuel_type || vehicle.fuel || vehicle.FuelType || "Unknown";

const getStatus = (vehicle) => String(vehicle.status || vehicle.Status || "").toUpperCase();

/** FULL CAPITAL / mixed labels → First capital, rest small (per word). */
const toTitleCaseLabel = (value) => {
  const text = String(value || "").trim();
  if (!text) return "Unknown";
  return text
    .toLowerCase()
    .replace(/\b([a-z])/g, (char) => char.toUpperCase());
};

const getNumericValue = (vehicle, keys) => {
  for (const key of keys) {
    const value = Number.parseFloat(vehicle?.[key]);
    if (Number.isFinite(value)) return value;
  }
  return 0;
};

const getOptionalNumericValue = (vehicle, keys) => {
  for (const key of keys) {
    const rawValue = vehicle?.[key];
    if (rawValue === undefined || rawValue === null || rawValue === "") continue;
    const value = Number.parseFloat(rawValue);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const getVehicleLabel = (vehicle) =>
  vehicle.name ||
  vehicle.vehicleName ||
  vehicle.VehicleName ||
  vehicle.VehicleNumber ||
  vehicle.vehicleNumber ||
  "N/A";

/** Adapt monitor-table device shape (`val`/`min`/`max`) for shared status helpers. */
const adaptMonitorDevice = (device = {}) => ({
  ...device,
  value: device.value ?? device.val,
  minValue: device.minValue ?? device.min,
  maxValue: device.maxValue ?? device.max,
  warningMargin: device.warningMargin ?? device.warningPercentage,
  sensorType: device.sensorType || device.type,
  deviceType: device.deviceType || (device.type ? "SENSOR" : device.deviceType),
});

const isCriticalMonitorDevice = (device = {}) => {
  const adapted = adaptMonitorDevice(device);
  if (isSensorAlert(adapted)) return true;
  // Non-sensor / on-off hardware marked dead or not working counts as critical.
  if (isOnOffDevice(adapted) || !adapted.sensorType) {
    const states = [adapted.health, adapted.status, adapted.workingCondition]
      .map((value) =>
        String(value || "")
          .trim()
          .toUpperCase()
          .replace(/[\s-]+/g, "_"),
      );
    if (states.some((state) => ["CRITICAL", "ALERT", "DEAD", "NOT_WORKING"].includes(state))) {
      return true;
    }
  }
  return false;
};

const isWarningMonitorDevice = (device = {}) => {
  const adapted = adaptMonitorDevice(device);
  if (isCriticalMonitorDevice(device)) return false;
  if (isSensorNearThreshold(adapted)) return true;
  if (isInactiveState(adapted)) return true;
  return false;
};

const isTelemetryMetricCritical = (key, value) => {
  const range = DEFAULT_SENSOR_RANGES[normalizeSensorType(key)];
  const number = Number(value);
  if (!range || !Number.isFinite(number) || number === 0) return false;
  return number < range.min || number > range.max;
};

const isTelemetryMetricWarning = (key, value) => {
  const range = DEFAULT_SENSOR_RANGES[normalizeSensorType(key)];
  const number = Number(value);
  if (!range || !Number.isFinite(number) || number === 0) return false;
  if (isTelemetryMetricCritical(key, value)) return false;
  const span = range.max - range.min;
  if (!(span > 0)) return false;
  const warning = span * 0.1;
  return number - range.min <= warning || range.max - number <= warning;
};

const getVehicleSensorHealth = (vehicle, devicesByVehicleId = {}, liveLocationsByVehicleId = {}) => {
  const vehicleId = String(vehicle?.id ?? "");
  const devices = devicesByVehicleId[vehicleId] || [];
  const telemetry = liveLocationsByVehicleId[vehicleId]?.telemetry || {};

  let criticalDevices = 0;
  let warningDevices = 0;

  devices.forEach((device) => {
    if (isCriticalMonitorDevice(device)) criticalDevices += 1;
    else if (isWarningMonitorDevice(device)) warningDevices += 1;
  });

  Object.entries(telemetry).forEach(([key, value]) => {
    if (isTelemetryMetricCritical(key, value)) criticalDevices += 1;
    else if (isTelemetryMetricWarning(key, value)) warningDevices += 1;
  });

  const hasCriticalSensor = criticalDevices > 0;
  const hasWarningSensor = warningDevices > 0;

  return {
    criticalDevices,
    warningDevices,
    hasCriticalSensor,
    hasWarningSensor,
  };
};

/**
 * Fleet Health buckets stay disjoint. Priority:
 * Critical (DEAD vehicle OR any critical sensor/device) >
 * Warning (INACTIVE OR near-threshold / inactive device) >
 * Offline (no status) >
 * Healthy
 */
const getFleetHealthBucket = (vehicle, sensorHealth) => {
  const status = getStatus(vehicle);
  if (status === "DEAD" || sensorHealth.hasCriticalSensor) return "critical";
  if (status === "INACTIVE" || sensorHealth.hasWarningSensor) return "warning";
  if (!status) return "offline";
  return "healthy";
};

const getSensorReading = (vehicle, devicesByVehicleId, liveLocationsByVehicleId, types = []) => {
  const vehicleId = String(vehicle?.id ?? "");
  const wanted = new Set(types.map(normalizeSensorType));
  const devices = devicesByVehicleId[vehicleId] || [];

  for (const device of devices) {
    const type = normalizeSensorType(device?.type || device?.sensorType);
    if (!wanted.has(type)) continue;
    const value = Number(device?.val ?? device?.value);
    if (Number.isFinite(value)) return value;
  }

  const telemetry = liveLocationsByVehicleId[vehicleId]?.telemetry || {};
  for (const type of wanted) {
    const value = Number(telemetry[type]);
    if (Number.isFinite(value) && value !== 0) return value;
  }

  return null;
};

const buildMetricRows = (vehicles, keys, limit = 8) =>
  vehicles
    .map((vehicle) => ({
      label: getVehicleLabel(vehicle),
      value: getOptionalNumericValue(vehicle, keys),
    }))
    .filter((row) => row.value !== null)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

const getTimeString = (row, keys = ["startTime", "timestamp", "time", "createdAt"]) => {
  const raw = keys.map((key) => row?.[key]).find(Boolean);
  if (!raw) return "";

  if (typeof raw === "string") {
    const timeMatch = raw.match(/(?:^|[T,\s])(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (timeMatch) {
      return `${timeMatch[1].padStart(2, "0")}:${timeMatch[2]}`;
    }
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";

  return parsed.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const fillHourlySeries = (hourlyValues) => {
  if (!hourlyValues.size) return [];

  const hours = Array.from(hourlyValues.keys()).sort();
  const startHour = Number.parseInt(hours[0], 10);
  const endHour = Number.parseInt(hours[hours.length - 1], 10);

  return Array.from({ length: endHour - startHour + 1 }, (_, index) => {
    const label = `${String(startHour + index).padStart(2, "0")}:00`;
    return {
      label,
      value: hourlyValues.get(label) || 0,
    };
  });
};

const buildTripsByTimeRows = (tripRows) => {
  const hourlyCounts = new Map();

  tripRows.forEach((row) => {
    const time = getTimeString(row);
    const hour = time.split(":")[0]?.padStart(2, "0");
    if (!hour) return;
    const label = `${hour}:00`;
    hourlyCounts.set(label, (hourlyCounts.get(label) || 0) + 1);
  });

  return fillHourlySeries(hourlyCounts);
};

const parseDurationToHours = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  const text = String(value).trim();
  const hms = text.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (hms) {
    return (
      Number(hms[1]) +
      Number(hms[2]) / 60 +
      Number(hms[3] || 0) / 3600
    );
  }

  const hours = Number(text.match(/(\d+(?:\.\d+)?)\s*h/i)?.[1] || 0);
  const minutes = Number(text.match(/(\d+(?:\.\d+)?)\s*m/i)?.[1] || 0);
  if (hours || minutes) return hours + minutes / 60;

  const numeric = Number.parseFloat(text);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getTripDurationHours = (row = {}) => {
  const explicit = parseDurationToHours(
    row.durationHours || row.durationInHours || row.totalHours || row.duration,
  );
  if (explicit > 0) return explicit;

  const start = getTimeString(row, ["startTime"]);
  const end = getTimeString(row, ["endTime"]);
  if (!start || !end) return 0;

  const [startHour, startMinute] = start.split(":").map(Number);
  const [endHour, endMinute] = end.split(":").map(Number);
  let startMinutes = startHour * 60 + startMinute;
  let endMinutes = endHour * 60 + endMinute;
  if (endMinutes < startMinutes) endMinutes += 24 * 60;
  return Math.max(endMinutes - startMinutes, 0) / 60;
};

const UTILIZATION_HOURS = Array.from({ length: 25 }, (_, hour) =>
  hour === 24 ? "24:00" : `${String(hour).padStart(2, "0")}:00`,
);

const parseStartMinutes = (row) => {
  const time = getTimeString(row);
  if (!time) return null;
  const [hour, minute] = time.split(":").map(Number);
  if (!Number.isFinite(hour)) return null;
  return hour * 60 + (Number.isFinite(minute) ? minute : 0);
};

const addDurationAcrossHours = (startMinutes, durationHours, hourlyHours) => {
  let remaining = durationHours * 60;
  let cursor = startMinutes;

  while (remaining > 0 && cursor < 24 * 60) {
    const hour = Math.floor(cursor / 60);
    const minutesLeftInHour = 60 - (cursor % 60);
    const chunk = Math.min(remaining, minutesLeftInHour);
    const label = `${String(hour).padStart(2, "0")}:00`;
    hourlyHours.set(label, (hourlyHours.get(label) || 0) + chunk / 60);
    remaining -= chunk;
    cursor += chunk;
  }
};

const buildUtilizationByTimeRows = (tripRows = [], vehicleCount = 0) => {
  if (!tripRows.length) return [];

  const hourlyHours = new Map();

  tripRows.forEach((row) => {
    const startMinutes = parseStartMinutes(row);
    const hours = getTripDurationHours(row);
    if (startMinutes == null || !(hours > 0)) return;
    addDurationAcrossHours(startMinutes, hours, hourlyHours);
  });

  const uniqueTripVehicles = new Set(
    tripRows
      .map((row) => row.vehicleName || row.vehicleNumber || row.deviceId || row.device_id)
      .filter(Boolean)
      .map(String),
  );
  const fleetSize = Math.max(vehicleCount, uniqueTripVehicles.size, 1);

  const hourlyPercents = UTILIZATION_HOURS.slice(0, 24).map((label) => {
    const movingHours = hourlyHours.get(label) || 0;
    return Math.max(0, Math.min(100, Math.round((movingHours / fleetSize) * 100)));
  });

  return UTILIZATION_HOURS.map((label, index) => ({
    label,
    value: index === 24 ? hourlyPercents[23] : hourlyPercents[index],
  }));
};

const getAverageUtilization = (rows = [], dateOption = "today") => {
  if (!rows.length) return 0;

  let relevant = rows.filter((row) => row.label !== "24:00");
  if (dateOption === "today") {
    const currentHour = new Date().getHours();
    relevant = relevant.filter((row) => {
      const hour = Number.parseInt(row.label, 10);
      return Number.isFinite(hour) && hour <= currentHour;
    });
  }

  if (!relevant.length) return 0;
  const total = relevant.reduce((sum, row) => sum + Number(row.value || 0), 0);
  return Math.round(total / relevant.length);
};

const buildVehicleUtilizationRows = (runtimeRows = [], limit = 8) =>
  runtimeRows
    .map((row) => {
      const moving = Number(row?.totalMovingHours);
      const runtime = Number(row?.totalRuntimeHours);
      const idle = Number(row?.totalIdleHours);
      const available =
        Number.isFinite(runtime) && runtime > 0
          ? runtime
          : (Number.isFinite(moving) ? moving : 0) + (Number.isFinite(idle) ? idle : 0);
      if (!(available > 0) || !Number.isFinite(moving)) return null;
      return {
        label: row.vehicleName || row.vehicleNumber || "N/A",
        value: Math.max(0, Math.min(100, Math.round((moving / available) * 100))),
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

const getFleetUtilizationPercent = (runtimeRows = []) => {
  const totals = runtimeRows.reduce(
    (acc, row) => {
      const moving = Number(row?.totalMovingHours);
      const runtime = Number(row?.totalRuntimeHours);
      const idle = Number(row?.totalIdleHours);
      if (Number.isFinite(moving)) acc.moving += moving;
      if (Number.isFinite(runtime) && runtime > 0) acc.available += runtime;
      else {
        acc.available +=
          (Number.isFinite(moving) ? moving : 0) + (Number.isFinite(idle) ? idle : 0);
      }
      return acc;
    },
    { moving: 0, available: 0 },
  );

  if (!(totals.available > 0)) return 0;
  return Math.max(0, Math.min(100, Math.round((totals.moving / totals.available) * 100)));
};

const groupBy = (items, getKey) =>
  items.reduce((acc, item) => {
    const key = getKey(item) || "Unknown";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

const toChartRows = (countMap, limit = 5) =>
  Object.entries(countMap)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

export const getReportRows = (report) => {
  if (Array.isArray(report?.data)) return report.data;
  if (Array.isArray(report?.sections)) {
    return report.sections.flatMap((section) =>
      Array.isArray(section?.data) ? section.data : [],
    );
  }
  return [];
};

const getDonutGradient = (rows) => {
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  if (!total) return "#e5e7eb";

  let cursor = 0;
  const segments = rows.map((row, index) => {
    const start = cursor;
    const size = (row.value / total) * 100;
    cursor += size;
    return `${row.color || COLORS[index % COLORS.length]} ${start}% ${cursor}%`;
  });

  return `conic-gradient(${segments.join(", ")})`;
};

function CardHeader({ title, action, onActionClick, extra }) {
  const { t } = useTranslation();
  return (
    <div className="vehicle-dashboard-panel-header">
      <h3>{t(title)}</h3>
      {extra || action ? (
        <div className="vehicle-dashboard-header-extras">
          {extra}
          {action ? (
            <button type="button" onClick={onActionClick}>
              {t(action)}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function FloatingChartTooltip({ tooltip }) {
  if (!tooltip?.text) return null;
  return (
    <div
      style={{
        ...CHART_TOOLTIP_STYLE,
        left: tooltip.x,
        top: tooltip.y,
      }}
    >
      {tooltip.text}
    </div>
  );
}

function useChartHoverTooltip() {
  const [tooltip, setTooltip] = useState(null);

  const showTooltip = (event, text) => {
    setTooltip({
      text,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const moveTooltip = (event) => {
    setTooltip((prev) =>
      prev
        ? {
            ...prev,
            x: event.clientX,
            y: event.clientY,
          }
        : prev,
    );
  };

  const hideTooltip = () => setTooltip(null);

  return { tooltip, showTooltip, moveTooltip, hideTooltip };
}

export const DonutPanel = memo(function DonutPanel({
  title,
  rows,
  total,
  action,
  onActionClick,
  extra,
  centerValue,
  centerLabel = "Total",
  className = "",
  emptyMessage = "No vehicles added yet",
}) {
  const { t } = useTranslation();
  const { tooltip, showTooltip, moveTooltip, hideTooltip } = useChartHoverTooltip();
  const displayTotal = total || rows.reduce((sum, row) => sum + (row.value || 0), 0);

  return (
    <section className={`vehicle-dashboard-card ${className}`.trim()}>
      <CardHeader title={title} action={action} onActionClick={onActionClick} extra={extra} />
      <div className="vehicle-dashboard-donut-layout">
        <div
          className="vehicle-dashboard-donut"
          style={{ "--vehicle-donut": getDonutGradient(rows) }}
          onMouseMove={moveTooltip}
          onMouseLeave={hideTooltip}
        >
          {displayTotal > 0 ? (
            <svg
              className="vehicle-dashboard-donut-hit"
              viewBox="0 0 100 100"
              onMouseLeave={hideTooltip}
            >
              {(() => {
                let cursor = 0;
                const radius = 40;
                const inner = 26;
                const cx = 50;
                const cy = 50;
                return rows.map((row) => {
                  if (!row.value) return null;
                  const startAngle = (cursor / displayTotal) * 360;
                  cursor += row.value;
                  const endAngle = (cursor / displayTotal) * 360;
                  const startRad = ((startAngle - 90) * Math.PI) / 180;
                  const endRad = ((endAngle - 90) * Math.PI) / 180;
                  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
                  const x1 = cx + radius * Math.cos(startRad);
                  const y1 = cy + radius * Math.sin(startRad);
                  const x2 = cx + radius * Math.cos(endRad);
                  const y2 = cy + radius * Math.sin(endRad);
                  const ix1 = cx + inner * Math.cos(endRad);
                  const iy1 = cy + inner * Math.sin(endRad);
                  const ix2 = cx + inner * Math.cos(startRad);
                  const iy2 = cy + inner * Math.sin(startRad);
                  const percent = ((row.value / displayTotal) * 100).toFixed(1);
                  const label = t(row.label);
                  return (
                    <path
                      key={row.label}
                      d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix1} ${iy1} A ${inner} ${inner} 0 ${largeArc} 0 ${ix2} ${iy2} Z`}
                      fill="transparent"
                      onMouseEnter={(event) =>
                        showTooltip(event, `${label}: ${row.value} (${percent}%)`)
                      }
                      onMouseMove={(event) =>
                        showTooltip(event, `${label}: ${row.value} (${percent}%)`)
                      }
                    />
                  );
                });
              })()}
            </svg>
          ) : null}
          <div>
            <strong>{centerValue ?? total ?? displayTotal}</strong>
            <span>{t(centerLabel || "Total")}</span>
          </div>
        </div>
        <div className="vehicle-dashboard-legend">
          {rows.length ? (
            rows.map((row, index) => {
              const percent = displayTotal
                ? ((row.value / displayTotal) * 100).toFixed(1)
                : "0.0";
              const label = t(row.label);
              return (
                <div
                  key={row.label}
                  onMouseEnter={(event) =>
                    showTooltip(event, `${label}: ${row.value} (${percent}%)`)
                  }
                  onMouseMove={(event) =>
                    showTooltip(event, `${label}: ${row.value} (${percent}%)`)
                  }
                  onMouseLeave={hideTooltip}
                >
                  <i style={{ backgroundColor: row.color || COLORS[index % COLORS.length] }} />
                  <span>{label}</span>
                  <strong>
                    {row.value} ({percent}%)
                  </strong>
                </div>
              );
            })
          ) : (
            <span className="vehicle-dashboard-empty">{t(emptyMessage)}</span>
          )}
        </div>
      </div>
      <FloatingChartTooltip tooltip={tooltip} />
    </section>
  );
});

export const BarPanel = memo(function BarPanel({
  title,
  rows,
  valueLabel = "vehicles",
  valueFormatter,
  action,
  onActionClick,
  extra,
  emptyMessage = "No data available yet",
  className = "",
}) {
  const { t } = useTranslation();
  const maxValue = Math.max(...rows.map((row) => row.value), 1);
  const { tooltip, showTooltip, moveTooltip, hideTooltip } = useChartHoverTooltip();

  return (
    <section className={`vehicle-dashboard-card ${className}`}>
      <CardHeader title={title} action={action} onActionClick={onActionClick} extra={extra} />
      <div className="vehicle-dashboard-bars">
        {rows.length ? (
          rows.map((row, index) => {
            const label = t(row.label);
            const displayValue = valueFormatter
              ? valueFormatter(row.value)
              : `${row.value}${valueLabel ? ` ${t(valueLabel)}` : ""}`;
            return (
              <div
                className="vehicle-dashboard-bar-row"
                key={`${row.label}-${index}`}
                onMouseEnter={(event) =>
                  showTooltip(event, `${label}: ${displayValue}`)
                }
                onMouseMove={moveTooltip}
                onMouseLeave={hideTooltip}
              >
                <div className="vehicle-dashboard-bar-label">
                  <span>{label}</span>
                  <strong>{displayValue}</strong>
                </div>
                <div className="vehicle-dashboard-bar-track">
                  <span
                    style={{
                      width: `${Math.max((row.value / maxValue) * 100, 4)}%`,
                      backgroundColor: row.color || "#3b82f6",
                    }}
                  />
                </div>
              </div>
            );
          })
        ) : (
          <span className="vehicle-dashboard-empty">{t(emptyMessage)}</span>
        )}
      </div>
      <FloatingChartTooltip tooltip={tooltip} />
    </section>
  );
});

export const AlertsPanel = memo(function AlertsPanel({
  title = "Vehicle Alerts Summary",
  rows = [],
  onViewAll,
  action = "View All",
  valueSuffix,
  singularSuffix,
  className = "",
  emptyMessage = "No data available yet",
}) {
  const { t } = useTranslation();
  return (
    <section className={`vehicle-dashboard-card ${className}`.trim()}>
      <CardHeader
        title={title}
        action={action}
        onActionClick={onViewAll}
      />
      <div className="vehicle-dashboard-alert-list">
        {rows.length ? (
          rows.map((row) => {
            const isZero = Number(row.value) === 0;
            const dotColor = isZero
              ? ALERT_COLORS.good
              : ALERT_COLORS[row.tone] || ALERT_COLORS.danger;
            const suffix = valueSuffix
              ? ` ${t(Number(row.value) === 1 ? singularSuffix || valueSuffix : valueSuffix)}`
              : "";
            return (
              <div key={row.label}>
                <i style={{ backgroundColor: dotColor }} />
                <span>{t(row.label)}</span>
                <strong>
                  {row.value}
                  {suffix}
                </strong>
              </div>
            );
          })
        ) : (
          <span className="vehicle-dashboard-empty">{t(emptyMessage)}</span>
        )}
      </div>
    </section>
  );
});

const compactSelectSx = {
  minWidth: 118,
  height: 28,
  fontSize: 10,
  fontWeight: 600,
  color: "#334155",
  backgroundColor: "#ffffff",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#dbe3ef",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cbd5e1",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#93c5fd",
  },
  "& .MuiSelect-select": {
    py: 0.5,
    px: 1.25,
  },
};

const compactDateFieldSx = {
  width: 132,
  "& .MuiInputBase-root": {
    height: 28,
    fontSize: 10,
    fontWeight: 600,
    color: "#334155",
    backgroundColor: "#ffffff",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#dbe3ef",
  },
  "& .MuiInputBase-input": {
    py: 0.5,
    px: 1,
  },
};

export function ChartDateSelect({
  dateOption,
  onDateOptionChange,
  customStartDate,
  customEndDate,
  onCustomRangeChange,
}) {
  const { t } = useTranslation();
  const userDateFormat = getUserDateFormat();
  const dateInputFormat = convertToDateFnsFormat(userDateFormat);
  const parseDate = (d) => {
    if (!d) return null;
    if (d instanceof Date) return isValid(d) ? d : null;
    const parsed = new Date(`${d}T00:00:00`);
    return isValid(parsed) ? parsed : null;
  };
  const normalizedValue = String(dateOption || "today")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ");

  return (
    <div className="vehicle-dashboard-trip-time-controls">
      <Select
        size="small"
        value={normalizedValue}
        onChange={(event) => onDateOptionChange(event.target.value)}
        sx={compactSelectSx}
      >
        {DASHBOARD_DATE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value} sx={{ fontSize: 12 }}>
            {t(option.label)}
          </MenuItem>
        ))}
      </Select>
      {normalizedValue === CUSTOM_DATE_OPTION && onCustomRangeChange ? (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={parseDate(customStartDate)}
            onChange={(newDate) => {
              const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
              onCustomRangeChange(val, customEndDate);
            }}
            maxDate={parseDate(customEndDate) || undefined}
            format={dateInputFormat}
            slotProps={{
              textField: {
                size: "small",
                placeholder: userDateFormat,
                sx: compactDateFieldSx,
              },
            }}
          />
          <span className="vehicle-dashboard-trip-time-range-sep">{t("to")}</span>
          <DatePicker
            value={parseDate(customEndDate)}
            minDate={parseDate(customStartDate) || undefined}
            onChange={(newDate) => {
              const val = newDate && isValid(newDate) ? format(newDate, "yyyy-MM-dd") : "";
              onCustomRangeChange(customStartDate, val);
            }}
            format={dateInputFormat}
            slotProps={{
              textField: {
                size: "small",
                placeholder: userDateFormat,
                sx: compactDateFieldSx,
              },
            }}
          />
        </LocalizationProvider>
      ) : null}
    </div>
  );
}

function TripsByTimeHeader({
  dateOption,
  onDateOptionChange,
  customStartDate,
  customEndDate,
  onCustomRangeChange,
}) {
  const { t } = useTranslation();

  return (
    <div className="vehicle-dashboard-panel-header vehicle-dashboard-trip-time-header">
      <h3>{t("Trips Start by Time of Day")}</h3>
      <ChartDateSelect
        dateOption={dateOption}
        onDateOptionChange={onDateOptionChange}
        customStartDate={customStartDate}
        customEndDate={customEndDate}
        onCustomRangeChange={onCustomRangeChange}
      />
    </div>
  );
}

function TripsByTimePanel({
  rows,
  dateOption,
  onDateOptionChange,
  customStartDate,
  customEndDate,
  onCustomRangeChange,
  loading,
  error,
}) {
  const { t } = useTranslation();
  const chartData = rows.map((row) => ({
    time: row.label,
    trips: row.value,
  }));

  const header = (
    <TripsByTimeHeader
      dateOption={dateOption}
      onDateOptionChange={onDateOptionChange}
      customStartDate={customStartDate}
      customEndDate={customEndDate}
      onCustomRangeChange={onCustomRangeChange}
    />
  );

  if (!rows.length) {
    const needsCustomRange =
      dateOption === CUSTOM_DATE_OPTION && !(customStartDate && customEndDate);

    return (
      <section className="vehicle-dashboard-card vehicle-dashboard-wide-card vehicle-dashboard-trip-time-card">
        {header}
        <span className="vehicle-dashboard-empty">
          {loading
            ? t("Loading trips…")
            : error
              ? t(error)
              : needsCustomRange
                ? t("Pick a start and end date to view trips")
                : t("No trips recorded for the selected period")}
        </span>
      </section>
    );
  }

  const maxValue = Math.max(...rows.map((row) => row.value), 1);
  const yMax = Math.max(4, Math.ceil(maxValue / 2) * 2);
  const yTicks = [0, yMax / 4, yMax / 2, (yMax * 3) / 4, yMax].map((tick) =>
    Math.round(tick),
  );

  return (
    <section className="vehicle-dashboard-card vehicle-dashboard-wide-card vehicle-dashboard-trip-time-card">
      {header}
      <div className="vehicle-dashboard-trip-line-chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 14, left: -18, bottom: 16 }}
          >
            <CartesianGrid stroke="#E5E7EB" strokeDasharray="2 2" />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              interval={Math.max(Math.ceil(chartData.length / 8) - 1, 0)}
              tick={{ fill: "#94A3B8", fontSize: 9, fontWeight: 600 }}
              dy={8}
            />
            <YAxis
              domain={[0, yMax]}
              ticks={yTicks}
              axisLine={false}
              tickLine={false}
              tick={{ fill: "#94A3B8", fontSize: 9, fontWeight: 600 }}
            />
            <Tooltip
              cursor={{ stroke: "#BFDBFE", strokeWidth: 1 }}
              contentStyle={{
                border: "1px solid #DBEAFE",
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                fontSize: 10,
                padding: "6px 8px",
              }}
              formatter={(value) => [`${value} ${t("trips")}`, t("Trips")]}
              labelFormatter={(label) => `${t("Time")}: ${label}`}
            />
            <Line
              type="monotone"
              dataKey="trips"
              stroke="#1E88E5"
              strokeWidth={2.2}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "#FFFFFF", fill: "#1E88E5" }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
        <span className="vehicle-dashboard-trip-axis-title">{t("Time (24hours)")}</span>
      </div>
    </section>
  );
}

function UtilizationByTimePanel({
  rows,
  percent,
  trend,
  loading,
  error,
  dateOption,
  onDateOptionChange,
  customStartDate,
  customEndDate,
  onCustomRangeChange,
}) {
  const { t } = useTranslation();
  const chartData = rows.map((row) => ({
    time: row.label,
    utilization: row.value,
  }));
  const hasTrend = Number.isFinite(trend) && trend !== 0;
  const trendUp = trend > 0;
  const needsCustomRange =
    dateOption === CUSTOM_DATE_OPTION && !(customStartDate && customEndDate);

  const header = (
    <div className="vehicle-dashboard-panel-header">
      <div className="vehicle-dashboard-trip-time-header">
        <h3>{t("Utilization Today")}</h3>
        <ChartDateSelect
          dateOption={dateOption}
          onDateOptionChange={onDateOptionChange}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onCustomRangeChange={onCustomRangeChange}
        />
      </div>
      <div className="vehicle-dashboard-utilization-metric">
        <strong>{percent}%</strong>
        <div className={`vehicle-dashboard-utilization-delta ${trendUp ? "up" : "down"}`}>
          {hasTrend ? (
            <span className="vehicle-dashboard-utilization-change">
              {trendUp ? "↑" : "↓"} {Math.abs(trend)}%
            </span>
          ) : null}
          <small>{t("AVERAGE")}</small>
        </div>
      </div>
    </div>
  );

  if (!rows.length) {
    return (
      <section className="vehicle-dashboard-card vehicle-dashboard-utilization-card">
        {header}
        <span className="vehicle-dashboard-empty">
          {loading
            ? t("Loading utilization…")
            : error
              ? t(error)
              : needsCustomRange
                ? t("Pick a start and end date to view utilization")
                : t("No vehicle utilization recorded for the selected period")}
        </span>
      </section>
    );
  }

  return (
    <section className="vehicle-dashboard-card vehicle-dashboard-utilization-card">
      {header}
      <div className="vehicle-dashboard-utilization-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{ top: 4, right: 4, left: -8, bottom: 0 }}
          >
            <defs>
              <linearGradient id="utilizationFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2F80ED" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#2F80ED" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#EEF2F7" vertical={false} />
            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              ticks={["00:00", "06:00", "12:00", "18:00", "24:00"]}
              tick={{ fill: "#94A3B8", fontSize: 10, fontWeight: 600 }}
              dy={6}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 50, 100]}
              axisLine={false}
              tickLine={false}
              tickFormatter={(value) => `${value}%`}
              tick={{ fill: "#94A3B8", fontSize: 10, fontWeight: 600 }}
              width={42}
            />
            <Tooltip
              cursor={{ stroke: "#BFDBFE", strokeWidth: 1 }}
              contentStyle={{
                border: "1px solid #DBEAFE",
                borderRadius: 4,
                boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
                fontSize: 10,
                padding: "6px 8px",
              }}
              formatter={(value) => [`${value}%`, t("Utilization")]}
              labelFormatter={(label) => `${t("Time")}: ${label}`}
            />
            <Area
              type="monotone"
              dataKey="utilization"
              stroke="#2F80ED"
              strokeWidth={2.4}
              fill="url(#utilizationFill)"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: "#FFFFFF", fill: "#2F80ED" }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}

const loadVehicleTripRows = (organizationId, siteId, period) =>
  reportsApi
    .getTripReport({
      orgId: organizationId || getOrganizationId(),
      siteId,
      category: "vehicles",
      selectedIds: [],
      selectAll: true,
      dateOption: period.dateOption,
      customStartDate: period.dateOption === CUSTOM_DATE_OPTION ? period.customStart : undefined,
      customEndDate: period.dateOption === CUSTOM_DATE_OPTION ? period.customEnd : undefined,
    })
    .then((report) => getReportRows(report));

function DashboardGraph({
  vehicleData = [],
  monitorDevicesByVehicleId = {},
  liveLocationsByVehicleId = {},
  overspeedLimit,
  overspeedIncidentCount = 0,
  organizationId,
  siteId,
  dateOption = "today",
  customStartDate = "",
  customEndDate = "",
  error: parentError = "",
}) {
  const navigate = useNavigate();
  const tripChart = useChartPeriod(dateOption, customStartDate, customEndDate);
  const utilizationChart = useChartPeriod(dateOption, customStartDate, customEndDate);
  const [tripRows, setTripRows] = useState([]);
  const [previousTripRows, setPreviousTripRows] = useState([]);
  const [runtimeRows, setRuntimeRows] = useState([]);
  const [isTripLoading, setIsTripLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const loadTripChart = useCallback(
    () => loadVehicleTripRows(organizationId, siteId, tripChart),
    [organizationId, siteId, tripChart.dateOption, tripChart.customStart, tripChart.customEnd],
  );
  const loadUtilizationChart = useCallback(
    () => loadVehicleTripRows(organizationId, siteId, utilizationChart),
    [
      organizationId,
      siteId,
      utilizationChart.dateOption,
      utilizationChart.customStart,
      utilizationChart.customEnd,
    ],
  );
  const tripChartReport = useChartValue(tripChart, tripRows, loadTripChart, EMPTY_CHART_ROWS);
  const utilizationChartReport = useChartValue(
    utilizationChart,
    tripRows,
    loadUtilizationChart,
    EMPTY_CHART_ROWS,
  );

  const vehicles = useMemo(
    () => (Array.isArray(vehicleData) ? vehicleData : []),
    [vehicleData],
  );

  useEffect(() => {
    let cancelled = false;
    const isCustom = dateOption === CUSTOM_DATE_OPTION;

    // The report API rejects a custom range unless both ends are supplied.
    if (isCustom && !(customStartDate && customEndDate)) {
      setTripRows([]);
      setPreviousTripRows([]);
      setRuntimeRows([]);
      setIsTripLoading(false);
      setReportError("");
      return undefined;
    }

    const fetchDashboardReports = async () => {
      setIsTripLoading(true);
      setReportError("");
      const reportParams = {
        orgId: organizationId || getOrganizationId(),
        siteId,
        category: "vehicles",
        selectedIds: [],
        selectAll: true,
        dateOption,
        customStartDate: isCustom ? customStartDate : undefined,
        customEndDate: isCustom ? customEndDate : undefined,
      };

      try {
        const [tripRes, runtimeRes] = await Promise.allSettled([
          reportsApi.getTripReport(reportParams),
          reportsApi.getRuntimeReport(reportParams),
        ]);

        if (cancelled) return;

        let detectedError = "";

        if (tripRes.status === "fulfilled") {
          setTripRows(getReportRows(tripRes.value));
        } else {
          setTripRows([]);
          const reason = tripRes.reason;
          detectedError =
            reason?.response?.data?.error ||
            reason?.response?.data?.message ||
            (reason?.response?.status === 404 ? "No data found for the selected period" : "");
          console.error("Failed to load dashboard trip report:", reason);
        }

        if (runtimeRes.status === "fulfilled") {
          setRuntimeRows(getReportRows(runtimeRes.value));
        } else {
          setRuntimeRows([]);
          const reason = runtimeRes.reason;
          if (!detectedError) {
            detectedError =
              reason?.response?.data?.error ||
              reason?.response?.data?.message ||
              (reason?.response?.status === 404 ? "No data found for the selected period" : "");
          }
          console.error("Failed to load dashboard runtime report:", reason);
        }

        if (detectedError) {
          setReportError(detectedError);
        }

        if (dateOption === "today") {
          try {
            const prev = await reportsApi.getTripReport({ ...reportParams, dateOption: "yesterday" });
            if (!cancelled) setPreviousTripRows(getReportRows(prev));
          } catch (_) {
            if (!cancelled) setPreviousTripRows([]);
          }
        } else {
          setPreviousTripRows([]);
        }
      } catch (error) {
        console.error("Failed to load dashboard reports:", error);
        if (!cancelled) {
          setTripRows([]);
          setRuntimeRows([]);
          const msg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            (error?.response?.status === 404 ? "No data found for the selected period" : "");
          if (msg) setReportError(msg);
        }
      } finally {
        if (!cancelled) {
          setIsTripLoading(false);
        }
      }
    };

    fetchDashboardReports();

    return () => {
      cancelled = true;
    };
  }, [
    organizationId,
    siteId,
    dateOption,
    customStartDate,
    customEndDate,
  ]);

  const dashboardData = useMemo(() => {
    const total = vehicles.length;
    let healthy = 0;
    let warning = 0;
    let critical = 0;
    let offline = 0;
    let criticalDeviceCount = 0;
    let inactive = 0;
    let highTemperature = 0;
    let lowPressure = 0;
    let lowFuel = 0;
    let humidity = 0;
    const routeCounts = {};

    vehicles.forEach((vehicle) => {
      const sensorHealth = getVehicleSensorHealth(
        vehicle,
        monitorDevicesByVehicleId,
        liveLocationsByVehicleId,
      );
      criticalDeviceCount += sensorHealth.criticalDevices;

      const bucket = getFleetHealthBucket(vehicle, sensorHealth);
      if (bucket === "critical") critical += 1;
      else if (bucket === "warning") warning += 1;
      else if (bucket === "offline") offline += 1;
      else healthy += 1;

      if (getStatus(vehicle) === "INACTIVE") inactive += 1;

      const temperature = getSensorReading(
        vehicle,
        monitorDevicesByVehicleId,
        liveLocationsByVehicleId,
        ["temperature"],
      );
      if (temperature !== null) {
        if (temperature > DEFAULT_SENSOR_RANGES.temperature.max) highTemperature += 1;
      } else if (
        getNumericValue(vehicle, ["temperature", "temp", "latestTemperature"]) > 45
      ) {
        highTemperature += 1;
      }

      const pressure = getSensorReading(
        vehicle,
        monitorDevicesByVehicleId,
        liveLocationsByVehicleId,
        ["pressure", "tyre_pressure"],
      );
      if (pressure !== null) {
        if (pressure < 30) lowPressure += 1;
      } else {
        const legacy = getOptionalNumericValue(vehicle, [
          "pressure",
          "tyrePressure",
          "latestPressure",
        ]);
        if (legacy !== null && legacy < 30) lowPressure += 1;
      }

      const fuel = getSensorReading(
        vehicle,
        monitorDevicesByVehicleId,
        liveLocationsByVehicleId,
        ["fuel", "fuel_level"],
      );
      if (fuel !== null) {
        if (fuel < DEFAULT_SENSOR_RANGES.fuel.min || fuel < 25) lowFuel += 1;
      } else {
        const legacy = getNumericValue(vehicle, [
          "fuelLevel",
          "fuel_percentage",
          "fuelPercent",
        ]);
        if (legacy > 0 && legacy < 25) lowFuel += 1;
      }

      const humidityValue = getSensorReading(
        vehicle,
        monitorDevicesByVehicleId,
        liveLocationsByVehicleId,
        ["humidity"],
      );
      if (humidityValue !== null) {
        const range = DEFAULT_SENSOR_RANGES.humidity;
        if (humidityValue < range.min || humidityValue > range.max) humidity += 1;
      }

      const routeName = getRouteName(vehicle);
      if (routeName) {
        routeCounts[routeName] = (routeCounts[routeName] || 0) + 1;
      }
    });

    const utilizationPercent = getFleetUtilizationPercent(runtimeRows);
    const utilizationRows = buildVehicleUtilizationRows(runtimeRows);
    const passengerRows = buildMetricRows(
      vehicles,
      ["passengerInflow", "passengerCount", "passengers", "totalPassengers"],
      7,
    );

    return {
      healthRows: [
        { label: "Healthy", value: healthy, color: "#16a34a" },
        { label: "Warning", value: warning, color: "#f59e0b" },
        { label: "Critical", value: critical, color: "#ef4444" },
        { label: "Offline", value: offline, color: "#94a3b8" },
      ],
      typeRows: toChartRows(
        groupBy(vehicles, (vehicle) => toTitleCaseLabel(getVehicleType(vehicle))),
        5,
      ),
      fuelRows: toChartRows(
        groupBy(vehicles, (vehicle) => toTitleCaseLabel(getFuelType(vehicle))),
        5,
      ),
      routeRows: toChartRows(routeCounts, 5),
      alertRows: [
        { label: "Critical Devices", value: criticalDeviceCount, tone: "danger" },
        { label: "Overspeed", value: overspeedIncidentCount, tone: "danger" },
        { label: "Inactive", value: inactive, tone: "warning" },
        { label: "High Temperature", value: highTemperature, tone: "danger" },
        { label: "Low Pressure", value: lowPressure, tone: "danger" },
        { label: "Low Fuel", value: lowFuel, tone: "danger" },
        { label: "Humidity", value: humidity, tone: "danger" },
      ],
      utilizationPercent,
      utilizationRows,
      passengerRows,
    };
  }, [
    vehicles,
    monitorDevicesByVehicleId,
    liveLocationsByVehicleId,
    overspeedIncidentCount,
    runtimeRows,
  ]);

  const tripsByTimeRows = useMemo(
    () => buildTripsByTimeRows(tripChartReport.value),
    [tripChartReport.value],
  );
  const utilizationByTimeRows = useMemo(
    () => buildUtilizationByTimeRows(utilizationChartReport.value, vehicles.length),
    [utilizationChartReport.value, vehicles.length],
  );
  const previousUtilizationRows = useMemo(
    () => buildUtilizationByTimeRows(previousTripRows, vehicles.length),
    [previousTripRows, vehicles.length],
  );
  const utilizationAverage = useMemo(
    () => getAverageUtilization(utilizationByTimeRows, utilizationChart.dateOption),
    [utilizationByTimeRows, utilizationChart.dateOption],
  );
  const utilizationTrend = useMemo(() => {
    if (!previousUtilizationRows.length) return 0;
    return utilizationAverage - getAverageUtilization(previousUtilizationRows, "yesterday");
  }, [utilizationAverage, previousUtilizationRows]);

  const handleViewAllAlerts = useCallback(
    () => navigate("/alert", { state: { alertTabKey: "vehicles" } }),
    [navigate],
  );

  return (
    <div className="vehicle-dashboard-grid">
      <section className="vehicle-dashboard-card vehicle-dashboard-live-card vehicle-dashboard-live-card--map">
        <div className="vehicle-dashboard-map vehicle-dashboard-map--reusable">
          <FleetMapView filterBy="routes" />
        </div>
      </section>

      <AlertsPanel
        rows={dashboardData.alertRows}
        onViewAll={handleViewAllAlerts}
      />
      <DonutPanel
        title="Fleet Health"
        rows={dashboardData.healthRows}
        total={vehicles.length}
        action="VIEW REPORT"
        onActionClick={() => navigate("/reports")}
      />
      <DonutPanel title="Vehicles by Type" rows={dashboardData.typeRows} total={vehicles.length} />
      <DonutPanel title="Vehicles by Fuel Type" rows={dashboardData.fuelRows} total={vehicles.length} />
      <UtilizationByTimePanel
        rows={utilizationByTimeRows}
        percent={
          utilizationChart.matchesPage
            ? utilizationAverage || dashboardData.utilizationPercent
            : utilizationAverage
        }
        trend={utilizationChart.matchesPage ? utilizationTrend : 0}
        loading={utilizationChart.matchesPage ? isTripLoading : utilizationChartReport.loading}
        error={
          utilizationChart.matchesPage
            ? parentError || reportError
            : utilizationChartReport.error
        }
        dateOption={utilizationChart.dateOption}
        onDateOptionChange={utilizationChart.setPeriod}
        customStartDate={utilizationChart.customStart}
        customEndDate={utilizationChart.customEnd}
        onCustomRangeChange={utilizationChart.setCustomRange}
      />
      <BarPanel
        title="Top Active Routes"
        rows={dashboardData.routeRows}
        action="VIEW ALL"
        onActionClick={() => navigate("/route")}
        emptyMessage="No routes assigned to vehicles yet"
      />
      <BarPanel
        title="Passenger Inflow"
        rows={dashboardData.passengerRows}
        valueLabel=""
        emptyMessage="No passenger activity recorded yet"
        className="vehicle-dashboard-wide-card vehicle-dashboard-passenger-card"
      />
      <TripsByTimePanel
        rows={tripsByTimeRows}
        dateOption={tripChart.dateOption}
        onDateOptionChange={tripChart.setPeriod}
        customStartDate={tripChart.customStart}
        customEndDate={tripChart.customEnd}
        onCustomRangeChange={tripChart.setCustomRange}
        loading={tripChart.matchesPage ? isTripLoading : tripChartReport.loading}
        error={tripChart.matchesPage ? parentError || reportError : tripChartReport.error}
      />
    </div>
  );
}

export default memo(DashboardGraph);
